import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Search,
  FilterList,
  AccountTree,
  Business,
  Timeline,
  Assessment,
  Visibility,
  Download,
  Share,
  ExpandMore,
  TrendingUp,
  TrendingDown,
  Warning,
  CheckCircle,
  Info,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import api from '../services/api';
import { LoggerService } from '../services/LoggerService';
import FundNetworkVisualization from '../components/visualization/FundNetworkVisualization';
import FundHierarchyVisualization from '../components/visualization/FundHierarchyVisualization';
import FundAnalytics from '../components/analytics/FundAnalytics';

interface Fund {
  id: string;
  name: string;
  legal_name?: string;
  lei: string;
  isin?: string;
  domicile: string;
  fund_type: string;
  total_assets: number;
  aum_eur?: number;
  currency: string;
  status: string;
  risk_level: string;
  inception_date?: string;
  cssf_registration_number?: string;
  prospectus_url?: string;
  annual_report_url?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface ServiceProvider {
  id: string;
  name: string;
  provider_type: string;
  country: string;
  license_number: string;
  status: string;
}

interface FundStructure {
  fund: Fund;
  serviceProviders: ServiceProvider[];
  relationships: Array<{
    fund_id: string;
    provider_id: string;
    relationship_type: string;
    start_date: string;
    end_date?: string;
  }>;
}

const FundExplorerPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [funds, setFunds] = useState<Fund[]>([]);
  const [fundStructures, setFundStructures] = useState<FundStructure[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFund, setSelectedFund] = useState<Fund | null>(null);
  const [selectedStructure, setSelectedStructure] = useState<FundStructure | null>(null);
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterRisk, setFilterRisk] = useState('all');
  const [viewMode, setViewMode] = useState<'list' | 'network' | 'hierarchy'>('network');

  useEffect(() => {
    loadFundData();
  }, []);

  const loadFundData = async () => {
    try {
      setLoading(true);
      
      // Load funds
      const fundsResponse = await api.get('/funds/');
      setFunds(fundsResponse.data.funds || []);

      // Load service providers from working endpoint
      try {
        const providersResponse = await api.get('/health/service-providers');
        LoggerService.debug('Service providers loaded:', providersResponse.data.length);
      } catch (error) {
        LoggerService.debug('Service providers not available:', error);
      }

      // Try to load fund structures (optional)
      try {
        const structuresResponse = await api.get('/funds/structures');
        setFundStructures(structuresResponse.data || []);
      } catch (error) {
        LoggerService.debug('Fund structures not available:', error);
        setFundStructures([]);
      }

    } catch (error: any) {
      LoggerService.error('Error loading fund data:', error);
      enqueueSnackbar('Failed to load fund data', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const filteredFunds = funds.filter(fund => {
    const matchesSearch = fund.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         fund.lei.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (fund.legal_name && fund.legal_name.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesType = filterType === 'all' || fund.fund_type === filterType;
    const matchesStatus = filterStatus === 'all' || fund.status === filterStatus;
    const matchesRisk = filterRisk === 'all' || fund.risk_level === filterRisk;

    return matchesSearch && matchesType && matchesStatus && matchesRisk;
  });

  const handleFundSelect = (fund: Fund) => {
    setSelectedFund(fund);
    const structure = fundStructures.find(s => s.fund.id === fund.id);
    setSelectedStructure(structure || null);
  };

  const getStatusColor = (status?: string) => {
    switch ((status || '').toLowerCase()) {
      case 'active': return 'success';
      case 'inactive': return 'error';
      case 'pending': return 'warning';
      default: return 'default';
    }
  };

  const getRiskColor = (risk?: string) => {
    switch ((risk || '').toLowerCase()) {
      case 'low': return 'success';
      case 'medium': return 'warning';
      case 'high': return 'error';
      default: return 'default';
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getStatusIcon = (status?: string) => {
    switch ((status || '').toLowerCase()) {
      case 'active': return <CheckCircle color="success" />;
      case 'inactive': return <Warning color="error" />;
      case 'pending': return <Info color="warning" />;
      default: return <Info />;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Loading Fund Explorer...
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Page Header */}
      <Box mb={4}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
          Fund Explorer
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Explore fund structures, analyze relationships, and visualize fund networks
        </Typography>
      </Box>

      {/* Search and Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Search Funds"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
                placeholder="Search by name, LEI, or manager..."
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Fund Type</InputLabel>
                <Select
                  value={filterType}
                  label="Fund Type"
                  onChange={(e) => setFilterType(e.target.value)}
                >
                  <MenuItem value="all">All Types</MenuItem>
                  <MenuItem value="UCITS">UCITS</MenuItem>
                  <MenuItem value="AIF">AIF</MenuItem>
                  <MenuItem value="SICAV">SICAV</MenuItem>
                  <MenuItem value="SICAR">SICAR</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filterStatus}
                  label="Status"
                  onChange={(e) => setFilterStatus(e.target.value)}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Risk Level</InputLabel>
                <Select
                  value={filterRisk}
                  label="Risk Level"
                  onChange={(e) => setFilterRisk(e.target.value)}
                >
                  <MenuItem value="all">All Risk</MenuItem>
                  <MenuItem value="low">Low</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Box display="flex" gap={1}>
                <Tooltip title="Network View">
                  <IconButton
                    color={viewMode === 'network' ? 'primary' : 'default'}
                    onClick={() => setViewMode('network')}
                  >
                    <AccountTree />
                  </IconButton>
                </Tooltip>
                <Tooltip title="List View">
                  <IconButton
                    color={viewMode === 'list' ? 'primary' : 'default'}
                    onClick={() => setViewMode('list')}
                  >
                    <FilterList />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Hierarchy View">
                  <IconButton
                    color={viewMode === 'hierarchy' ? 'primary' : 'default'}
                    onClick={() => setViewMode('hierarchy')}
                  >
                    <Timeline />
                  </IconButton>
                </Tooltip>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Results Summary */}
      <Box mb={3}>
        <Typography variant="h6" gutterBottom>
          {filteredFunds.length} funds found
        </Typography>
        <Box display="flex" gap={1} flexWrap="wrap">
          <Chip
            icon={<TrendingUp />}
            label={`${funds.filter(f => f.status === 'active').length} Active`}
            color="success"
            variant="outlined"
          />
          <Chip
            icon={<TrendingDown />}
            label={`${funds.filter(f => f.status === 'inactive').length} Inactive`}
            color="error"
            variant="outlined"
          />
          <Chip
            icon={<Assessment />}
            label={`${formatCurrency(funds.reduce((sum, f) => sum + f.total_assets, 0))} Total AUM`}
            color="primary"
            variant="outlined"
          />
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Fund List */}
        <Grid item xs={12} md={selectedFund ? 6 : 12}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">Funds</Typography>
                <Box>
                  <Tooltip title="Export Data">
                    <IconButton size="small">
                      <Download />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Share View">
                    <IconButton size="small">
                      <Share />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              {viewMode === 'list' ? (
                <List>
                  {filteredFunds.map((fund) => (
                    <ListItem
                      key={fund.id}
                      button
                      onClick={() => handleFundSelect(fund)}
                      selected={selectedFund?.id === fund.id}
                      sx={{
                        borderRadius: 1,
                        mb: 1,
                        '&.Mui-selected': {
                          backgroundColor: 'primary.light',
                          '&:hover': {
                            backgroundColor: 'primary.main',
                          },
                        },
                      }}
                    >
                      <ListItemIcon>
                        {getStatusIcon(fund.status)}
                      </ListItemIcon>
                      <ListItemText
                        primary={fund.name}
                        secondary={
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              LEI: {fund.lei} • {fund.fund_type}
                            </Typography>
                            <Box display="flex" gap={1} mt={0.5}>
                              <Chip
                                label={fund.status}
                                size="small"
                                color={getStatusColor(fund.status) as any}
                                variant="outlined"
                              />
                              <Chip
                                label={fund.risk_level}
                                size="small"
                                color={getRiskColor(fund.risk_level) as any}
                                variant="outlined"
                              />
                              <Chip
                                label={formatCurrency(fund.total_assets)}
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                            </Box>
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Grid container spacing={2}>
                  {filteredFunds.map((fund) => (
                    <Grid item xs={12} sm={6} md={4} key={fund.id}>
                      <Card
                        sx={{
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          '&:hover': {
                            borderColor: 'primary.main',
                          },
                          border: selectedFund?.id === fund.id ? 2 : 1,
                          borderColor: selectedFund?.id === fund.id ? 'primary.main' : 'divider',
                        }}
                        onClick={() => handleFundSelect(fund)}
                      >
                        <CardContent>
                          <Box display="flex" alignItems="center" mb={2}>
                            {getStatusIcon(fund.status)}
                            <Typography variant="h6" sx={{ ml: 1, flexGrow: 1 }}>
                              {fund.name}
                            </Typography>
                          </Box>
                          
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            {fund.fund_type} • {fund.domicile}
                          </Typography>
                          
                          <Typography variant="body2" gutterBottom>
                            LEI: {fund.lei}
                          </Typography>
                          
                          <Box display="flex" gap={1} mb={2} flexWrap="wrap">
                            <Chip
                              label={fund.status}
                              size="small"
                              color={getStatusColor(fund.status) as any}
                              variant="outlined"
                            />
                            <Chip
                              label={fund.risk_level}
                              size="small"
                              color={getRiskColor(fund.risk_level) as any}
                              variant="outlined"
                            />
                          </Box>
                          
                          <Typography variant="h6" color="primary">
                            {formatCurrency(fund.total_assets)}
                          </Typography>
                          
                          <Typography variant="body2" color="text.secondary">
                            CSSF: {fund.cssf_registration_number || 'N/A'}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Fund Details and Structure */}
        {selectedFund && (
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6">Fund Structure</Typography>
                  <Box>
                    <Tooltip title="View Details">
                      <IconButton size="small">
                        <Visibility />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Export Structure">
                      <IconButton size="small">
                        <Download />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>

                <Divider sx={{ mb: 2 }} />

                {/* Fund Information */}
                <Accordion defaultExpanded>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      Fund Information
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Name
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {selectedFund.name}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          LEI
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {selectedFund.lei}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Type
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {selectedFund.fund_type}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Domicile
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {selectedFund.domicile}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Total Assets
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500, color: 'primary.main' }}>
                          {formatCurrency(selectedFund.total_assets)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          CSSF Registration
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {selectedFund.cssf_registration_number || 'N/A'}
                        </Typography>
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>

                {/* Service Providers */}
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      Service Providers
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    {selectedStructure && selectedStructure.serviceProviders.length > 0 ? (
                      <List>
                        {selectedStructure.serviceProviders.map((provider) => (
                          <ListItem key={provider.id}>
                            <ListItemIcon>
                              <Business color="primary" />
                            </ListItemIcon>
                            <ListItemText
                              primary={provider.name}
                              secondary={
                                <Box>
                                  <Typography variant="body2" color="text.secondary">
                                    {provider.provider_type} • {provider.country}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    License: {provider.license_number}
                                  </Typography>
                                </Box>
                              }
                            />
                            <Chip
                              label={provider.status}
                              size="small"
                              color={getStatusColor(provider.status) as any}
                              variant="outlined"
                            />
                          </ListItem>
                        ))}
                      </List>
                    ) : (
                      <Alert severity="info">
                        No service providers found for this fund.
                      </Alert>
                    )}
                  </AccordionDetails>
                </Accordion>

                {/* Interactive Network Visualization */}
                <Accordion defaultExpanded>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      Interactive Network Visualization
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <FundNetworkVisualization
                      fundStructures={fundStructures}
                      selectedFund={selectedFund}
                      onNodeClick={(node) => {
                        LoggerService.debug('Node clicked:', node);
                        // Handle node click - could show details, highlight, etc.
                      }}
                      onLinkClick={(link) => {
                        LoggerService.debug('Link clicked:', link);
                        // Handle link click - could show relationship details
                      }}
                    />
                  </AccordionDetails>
                </Accordion>

                {/* Advanced Analytics */}
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      Advanced Analytics
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <FundAnalytics
                      funds={funds}
                      fundStructures={fundStructures}
                    />
                  </AccordionDetails>
                </Accordion>

                {/* Hierarchy Visualization */}
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      Fund Hierarchy Structure
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <FundHierarchyVisualization
                      fundStructures={fundStructures}
                      selectedFund={selectedFund}
                      onNodeClick={(node) => {
                        LoggerService.debug('Hierarchy node clicked:', node);
                        // Handle hierarchy node click
                      }}
                    />
                  </AccordionDetails>
                </Accordion>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Feature Status Alert */}
      <Alert severity="success" sx={{ mt: 3 }}>
        <Typography variant="body2">
          <strong>Fund Explorer - Fully Implemented!</strong> This page provides comprehensive fund exploration with 
          interactive network diagrams, hierarchy visualization, advanced analytics, search & filtering, 
          and detailed fund information with service provider relationships.
        </Typography>
      </Alert>
    </Box>
  );
};

export default FundExplorerPage;
